import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { FileText, Tag, CheckCircle, Clock, Award, ArrowLeft, Shield, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to strip HTML tags for preview text
const stripHtml = (html) => {
  if (!html) return '';
  try {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  } catch (e) {
    return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }
};

// Helper function to check if content contains HTML
const containsHtml = (str) => {
  if (!str) return false;
  return /<[a-z][\s\S]*>/i.test(str);
};

// Helper function to sanitize HTML and fix common issues
const sanitizeHtml = (html) => {
  if (!html) return '';
  
  try {
    // Create a temporary div to parse the HTML
    const temp = document.createElement('div');
    temp.innerHTML = html;
    
    // Remove any script tags for security
    const scripts = temp.querySelectorAll('script');
    scripts.forEach(s => s.remove());
    
    // Remove any onclick, onerror, etc. event handlers
    const allElements = temp.querySelectorAll('*');
    allElements.forEach(el => {
      Array.from(el.attributes).forEach(attr => {
        if (attr.name.startsWith('on')) {
          el.removeAttribute(attr.name);
        }
      });
    });
    
    return temp.innerHTML;
  } catch (e) {
    console.error('Error sanitizing HTML:', e);
    // If parsing fails, strip all HTML and return plain text
    return html.replace(/<[^>]*>/g, '');
  }
};

// Error boundary component for safe HTML rendering
const SafeHtmlRenderer = ({ html, className }) => {
  const [hasError, setHasError] = useState(false);
  const [sanitizedHtml, setSanitizedHtml] = useState('');

  useEffect(() => {
    try {
      const clean = sanitizeHtml(html);
      setSanitizedHtml(clean);
      setHasError(false);
    } catch (e) {
      console.error('Error processing HTML:', e);
      setHasError(true);
    }
  }, [html]);

  if (hasError || !sanitizedHtml) {
    // Fallback to plain text if HTML processing fails
    const plainText = html ? html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() : '';
    return <p className={className}>{plainText}</p>;
  }

  return (
    <div 
      className={className}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
};

export default function ViewQuestions() {
  const { jobTitle } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedQuestions, setExpandedQuestions] = useState({});

  // Decode the URL-encoded job title (handles special chars like "/" which are encoded as %2F)
  const decodedJobTitleFromUrl = decodeURIComponent(jobTitle || '');

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        
        if (!token || !email) {
          setError('Access token and email required. Please use the link from your email.');
          setLoading(false);
          return;
        }
        
        // The job title in URL is base64 encoded - pass it directly to backend
        // Backend will handle the decoding (with fallback for legacy URLs)
        const encodedJobTitle = decodedJobTitleFromUrl;
        
        const response = await axios.get(
          `${API}/interview-questions/view-role/${encodeURIComponent(encodedJobTitle)}?token=${token}&email=${email}`
        );
        
        if (response.data.has_access) {
          setQuestions(response.data.questions);
          // Expand all by default
          const expanded = {};
          response.data.questions.forEach((_, idx) => {
            expanded[idx] = true;
          });
          setExpandedQuestions(expanded);
        } else {
          setError('You do not have permission to view these questions.');
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load questions. You may not have access.');
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [decodedJobTitleFromUrl, token, email]);

  // Prevent screenshots - Enhanced protection for desktop/laptop
  // Note: Reduced aggressiveness on mobile to prevent blank page issues
  useEffect(() => {
    // Detect if mobile device
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    // Disable right-click
    const handleContextMenu = (e) => e.preventDefault();
    
    // Disable print screen and screenshot shortcuts (Windows, Mac, Linux)
    const handleKeyDown = (e) => {
      // PrintScreen, Snipping Tool shortcuts, Mac screenshot shortcuts
      if (e.key === 'PrintScreen' || 
          (e.ctrlKey && e.key === 'p') || 
          (e.ctrlKey && e.shiftKey && e.key === 's') ||
          (e.ctrlKey && e.shiftKey && e.key === '4') ||
          (e.metaKey && e.shiftKey && e.key === '4') ||
          (e.metaKey && e.shiftKey && e.key === '3') ||
          (e.metaKey && e.shiftKey && e.key === '5') ||
          // Windows Snipping Tool shortcuts
          (e.key === 's' && e.shiftKey && (e.metaKey || e.ctrlKey)) ||
          // F12 DevTools
          e.key === 'F12' ||
          // Ctrl+Shift+I (DevTools)
          (e.ctrlKey && e.shiftKey && e.key === 'i') ||
          (e.ctrlKey && e.shiftKey && e.key === 'I') ||
          // Ctrl+Shift+C (Inspect Element)
          (e.ctrlKey && e.shiftKey && e.key === 'c') ||
          (e.ctrlKey && e.shiftKey && e.key === 'C') ||
          // Ctrl+U (View Source)
          (e.ctrlKey && e.key === 'u') ||
          (e.ctrlKey && e.key === 'U')) {
        e.preventDefault();
        e.stopPropagation();
        alert('This action is disabled for security reasons.');
        return false;
      }
    };

    // Disable selection
    const handleSelectStart = (e) => e.preventDefault();
    
    // Add blur effect when window loses focus - DESKTOP ONLY
    const handleVisibilityChange = () => {
      if (isMobile) return; // Skip blur on mobile
      
      const container = document.getElementById('protected-content');
      if (container) {
        if (document.hidden) {
          container.style.filter = 'blur(30px)';
          container.style.opacity = '0.3';
        } else {
          container.style.filter = 'none';
          container.style.opacity = '1';
        }
      }
    };

    // Blur when window loses focus - DESKTOP ONLY
    let hasInteracted = false;
    const handleInteraction = () => { hasInteracted = true; };
    
    const handleWindowBlur = () => {
      if (isMobile) return; // Skip blur on mobile - causes blank page issues
      if (!hasInteracted) return;
      
      const container = document.getElementById('protected-content');
      if (container) {
        container.style.filter = 'blur(30px)';
        container.style.opacity = '0.3';
        container.style.transition = 'filter 0.05s ease, opacity 0.05s ease';
      }
    };

    const handleWindowFocus = () => {
      const container = document.getElementById('protected-content');
      if (container) {
        // Small delay before removing blur to catch quick screenshots
        setTimeout(() => {
          if (document.hasFocus()) {
            container.style.filter = 'none';
            container.style.opacity = '1';
          }
        }, isMobile ? 0 : 200); // No delay on mobile
      }
    };

    // Disable drag events
    const handleDragStart = (e) => e.preventDefault();
    
    // DevTools detection - DESKTOP ONLY
    const detectDevTools = () => {
      if (isMobile) return; // Skip on mobile
      
      const widthThreshold = window.outerWidth - window.innerWidth > 160;
      const heightThreshold = window.outerHeight - window.innerHeight > 160;
      const container = document.getElementById('protected-content');
      
      if ((widthThreshold || heightThreshold) && container) {
        container.style.filter = 'blur(30px)';
        container.style.opacity = '0.3';
      }
    };

    // Check for DevTools on resize - DESKTOP ONLY
    const handleResize = () => {
      if (isMobile) return;
      if (hasInteracted) {
        detectDevTools();
      }
    };
    
    // Ensure content is visible on initial load
    setTimeout(() => {
      const container = document.getElementById('protected-content');
      if (container) {
        container.style.filter = 'none';
        container.style.opacity = '1';
      }
    }, 100);

    // Add CSS to prevent screenshots via media queries
    const style = document.createElement('style');
    style.textContent = `
      @media print {
        #protected-content { display: none !important; }
        body::after { 
          content: 'Printing is disabled for this content.'; 
          display: block; 
          text-align: center; 
          padding: 50px; 
          font-size: 24px; 
        }
      }
      /* Attempt to detect screen capture - blur when not actively viewed */
      #protected-content {
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        user-select: none !important;
      }
      /* Make images non-draggable */
      #protected-content img {
        -webkit-user-drag: none;
        -khtml-user-drag: none;
        -moz-user-drag: none;
        -o-user-drag: none;
        user-drag: none;
        pointer-events: none;
      }
    `;
    document.head.appendChild(style);

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('selectstart', handleSelectStart);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('click', handleInteraction);
    document.addEventListener('scroll', handleInteraction);
    document.addEventListener('mousemove', handleInteraction);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('resize', handleResize);

    // Periodic check for DevTools
    const devToolsInterval = setInterval(detectDevTools, 1000);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('selectstart', handleSelectStart);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('scroll', handleInteraction);
      document.removeEventListener('mousemove', handleInteraction);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('resize', handleResize);
      clearInterval(devToolsInterval);
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    };
  }, []);

  const toggleQuestion = (idx) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading questions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl">
          <CardContent className="pt-6 text-center">
            <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button onClick={() => navigate('/')} className="bg-emerald-600 hover:bg-emerald-700">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Use the already decoded job title for display
  const displayJobTitle = decodedJobTitleFromUrl.replace(/-/g, ' ');

  return (
    <div 
      className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4 protected-view relative"
      style={{ 
        userSelect: 'none', 
        WebkitUserSelect: 'none', 
        MozUserSelect: 'none', 
        msUserSelect: 'none',
        WebkitTouchCallout: 'none'
      }}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onPaste={(e) => e.preventDefault()}
    >
      {/* Watermark overlay - more visible for deterrence */}
      <div 
        className="fixed inset-0 pointer-events-none z-50 overflow-hidden select-none"
        style={{ opacity: 0.06 }}
        aria-hidden="true"
      >
        <div className="absolute inset-0 flex flex-wrap items-center justify-center gap-16" style={{ transform: 'rotate(-30deg)', transformOrigin: 'center' }}>
          {[...Array(80)].map((_, i) => (
            <span key={i} className="text-gray-900 text-xl font-bold whitespace-nowrap">
              CONFIDENTIAL • DO NOT COPY • OFFICIUM CONNECT
            </span>
          ))}
        </div>
      </div>

      {/* Secondary diagonal watermark pattern */}
      <div 
        className="fixed inset-0 pointer-events-none z-40 overflow-hidden select-none"
        style={{ opacity: 0.04 }}
        aria-hidden="true"
      >
        <div className="absolute inset-0 flex flex-wrap items-center justify-center gap-24" style={{ transform: 'rotate(30deg)', transformOrigin: 'center' }}>
          {[...Array(60)].map((_, i) => (
            <span key={i} className="text-red-900 text-lg font-semibold whitespace-nowrap">
              PROTECTED CONTENT • {new Date().toLocaleDateString()}
            </span>
          ))}
        </div>
      </div>

      {/* Security Notice */}
      <div className="max-w-4xl mx-auto mb-4">
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center gap-3">
          <Shield className="h-5 w-5 text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-700">
            This is a secure, read-only view. Screenshots and copying are disabled.
          </p>
        </div>
      </div>

      <div id="protected-content" className="max-w-4xl mx-auto transition-all duration-300">
        {/* Header */}
        <Card className="shadow-xl border-0 overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6">
            <div className="flex items-center gap-3 text-white mb-2">
              <div className="h-12 w-12 bg-white/20 rounded-xl flex items-center justify-center">
                <FileText className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Interview Questions</h1>
                <p className="text-white/80 text-sm">Officium Connect</p>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <div>
                <p className="text-white/90 text-lg font-medium">{displayJobTitle}</p>
                <p className="text-white/70 text-sm">{questions.length} Questions</p>
              </div>
              <Badge className="bg-white/20 text-white text-sm px-3 py-1">
                All Questions View
              </Badge>
            </div>
          </div>
        </Card>

        {/* Questions List */}
        <div className="space-y-4">
          {questions.map((question, idx) => (
            <Card key={question.id || idx} className="shadow-lg border-0 overflow-hidden">
              {/* Question Header */}
              <div 
                className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 cursor-pointer flex items-center justify-between"
                onClick={() => toggleQuestion(idx)}
              >
                <div className="flex items-center gap-3">
                  <span className="h-8 w-8 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-sm">
                    {idx + 1}
                  </span>
                  <div>
                    <p className="font-medium text-gray-800 line-clamp-1">
                      {stripHtml(question.question)?.substring(0, 80)}...
                    </p>
                    <div className="flex gap-2 mt-1">
                      <Badge className={`text-xs ${
                        question.difficulty === 'Easy' ? 'bg-green-100 text-green-700' :
                        question.difficulty === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {question.difficulty}
                      </Badge>
                      <Badge className="text-xs bg-blue-100 text-blue-700">{question.category}</Badge>
                    </div>
                  </div>
                </div>
                {expandedQuestions[idx] ? (
                  <ChevronUp className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500" />
                )}
              </div>

              {/* Question Content */}
              {expandedQuestions[idx] && (
                <CardContent className="p-6 space-y-4">
                  {/* Question Images - Multiple */}
                  {((question.images && question.images.length > 0) || question.image_url) && (
                    <div className="flex flex-wrap gap-3">
                      {(question.images && question.images.length > 0 ? question.images : [question.image_url]).map((imgUrl, imgIdx) => (
                        <div key={imgIdx} className="rounded-xl overflow-hidden border-2 border-gray-100 shadow-md">
                          <img 
                            src={imgUrl.startsWith('http') ? imgUrl : `${API.replace('/api', '')}${imgUrl}`}
                            alt={`Question ${imgIdx + 1}`}
                            className="max-h-48 object-contain bg-gray-50"
                            draggable="false"
                            onDragStart={(e) => e.preventDefault()}
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Question Text */}
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-xl p-5">
                    {containsHtml(question.question) ? (
                      <SafeHtmlRenderer 
                        html={question.question}
                        className="text-gray-800 leading-relaxed prose prose-sm max-w-none [&_u]:underline [&_font]:text-inherit"
                      />
                    ) : (
                      <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">
                        {question.question}
                      </p>
                    )}
                  </div>

                  {/* MCQ Options */}
                  {question.question_type === 'multiple_choice' && question.options && (
                    <div className="bg-blue-50 p-4 rounded-xl">
                      <h4 className="font-semibold text-blue-700 mb-3 flex items-center gap-2 text-sm">
                        <CheckCircle className="h-4 w-4" />
                        Answer Options
                      </h4>
                      <div className="space-y-2">
                        {question.options.filter(opt => opt).map((opt, optIdx) => (
                          <div 
                            key={optIdx} 
                            className={`flex items-center gap-3 p-3 rounded-lg ${
                              question.correct_option === optIdx 
                                ? 'bg-green-100 border-2 border-green-400' 
                                : 'bg-white border border-gray-200'
                            }`}
                          >
                            <span className={`h-6 w-6 rounded-full flex items-center justify-center font-bold text-xs ${
                              question.correct_option === optIdx 
                                ? 'bg-green-500 text-white' 
                                : 'bg-gray-200 text-gray-600'
                            }`}>
                              {String.fromCharCode(65 + optIdx)}
                            </span>
                            <span className="text-gray-800 text-sm flex-1">{opt}</span>
                            {question.correct_option === optIdx && (
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Expected Answer */}
                  {question.expected_answer && (
                    <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                      <h4 className="font-semibold text-green-700 mb-2 flex items-center gap-2 text-sm">
                        <CheckCircle className="h-4 w-4" />
                        Expected Answer
                      </h4>
                      {containsHtml(question.expected_answer) ? (
                        <SafeHtmlRenderer 
                          html={question.expected_answer}
                          className="text-green-600 text-sm prose prose-sm max-w-none [&_u]:underline [&_font]:text-inherit"
                        />
                      ) : (
                        <p className="text-green-600 text-sm whitespace-pre-wrap">{question.expected_answer}</p>
                      )}
                    </div>
                  )}

                  {/* Tags & Meta */}
                  <div className="flex flex-wrap items-center gap-2 pt-2">
                    {question.points > 1 && (
                      <Badge className="bg-purple-100 text-purple-700 text-xs">
                        <Award className="h-3 w-3 mr-1" /> {question.points} pts
                      </Badge>
                    )}
                    {question.time_limit && (
                      <Badge className="bg-gray-100 text-gray-700 text-xs">
                        <Clock className="h-3 w-3 mr-1" /> {question.time_limit}s
                      </Badge>
                    )}
                    {question.tags?.map((tag, i) => (
                      <Badge key={i} className="bg-purple-100 text-purple-700 text-xs">
                        <Tag className="h-3 w-3 mr-1" />
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-gray-400 mt-8 pb-4">
          Shared via Officium Connect • Read-only secure access
        </div>
      </div>
    </div>
  );
}
